import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import { listCases, type CaseRecordData, type ListCasesOptions } from '@/lib/cases';
import { dataCivilEmUtc, diaCivilDeHoje } from '@/lib/data_civil';

import { derivedUiStatus } from './url_state';

/**
 * A ordem da lista de Casos (owner, 03/11): o "Prioritário" que a equipe marca no topo; depois os
 * casos em andamento com prazo informado, do prazo mais próximo ao mais distante, com a marca do
 * prazo; e o resto do caso mais antigo ao mais novo. O caso encerrado não sobe pelo prazo: o prazo
 * dele já não pede nada. Entre iguais decide a entrada (o mais antigo primeiro).
 *
 * A régua mora aqui, e não no servidor: a home lê a mesma rota pedindo os mais novos, e "encerrado"
 * é a situação que a tela deriva (`derivedUiStatus`).
 */

// E224.F1 (ADR-0151): o assinado entrou (aparecia com "Atrasado" no topo) e o eletrônico concluído
// saiu — ele aguarda a assinatura, e o prazo dele ainda pede o trabalho.
const ENCERRADOS: ReadonlySet<string> = new Set([
  'concluido',
  'finalizado',
  'encerrado_triagem',
  'encerrado_com_relatorio',
  'assinado',
  // E224.F2: o caso cancelado não tem prazo a cumprir.
  'cancelado',
]);

const DIA_MS = 24 * 60 * 60 * 1000;

/** O prazo que ordena e se sinaliza: o informado, no caso que ainda não se encerrou. */
export function prazoQueConta(c: CaseRecordData): string | null {
  if (c.deadline === null || c.deadline === undefined) return null;
  return ENCERRADOS.has(derivedUiStatus(c)) ? null : c.deadline;
}

function faixa(c: CaseRecordData): number {
  if (c.prioritario === true) return 0;
  return prazoQueConta(c) !== null ? 1 : 2;
}

function porPrazo(a: CaseRecordData, b: CaseRecordData): number {
  const pa = prazoQueConta(a);
  const pb = prazoQueConta(b);
  if (pa === null || pb === null) return Number(pa === null) - Number(pb === null);
  return pa.localeCompare(pb);
}

export function ordemDaLista(casos: readonly CaseRecordData[]): CaseRecordData[] {
  return [...casos].sort(
    (a, b) =>
      faixa(a) - faixa(b) ||
      porPrazo(a, b) ||
      a.created_at.localeCompare(b.created_at) ||
      a.display_code.localeCompare(b.display_code),
  );
}

export interface MarcaDoPrazo {
  texto: string;
  tom: 'danger' | 'warning' | 'neutral';
}

/** "Atrasado", "Vence hoje", "Faltam N dias", no dia civil de quem opera (E176.F1.C01). */
export function marcaDoPrazo(c: CaseRecordData, agora: Date): MarcaDoPrazo | null {
  const prazo = prazoQueConta(c);
  if (prazo === null) return null;
  const dias = Math.floor(
    (dataCivilEmUtc(prazo).getTime() - diaCivilDeHoje(agora).getTime()) / DIA_MS,
  );
  const t = ptBR.homePanel;
  if (dias < 0) return { texto: t.prazoOverdue, tom: 'danger' };
  if (dias === 0) return { texto: t.prazoToday, tom: 'warning' };
  if (dias === 1) return { texto: t.prazoTomorrow, tom: 'neutral' };
  return { texto: t.prazoDaysAway.replace('{{days}}', String(dias)), tom: 'neutral' };
}

const LOTE = 200;
const MAX_LOTES = 25;

/**
 * Todos os casos, em lotes do teto da rota (200). A lista pedia só o primeiro lote padrão (50, os
 * mais novos): com a ordem do mais antigo ao mais novo, os casos novos ficariam de fora.
 */
export async function listarTodosOsCasos(
  client: AxiosInstance,
  options: ListCasesOptions,
): Promise<CaseRecordData[]> {
  const porId = new Map<string, CaseRecordData>();
  for (let lote = 0; lote < MAX_LOTES; lote += 1) {
    const casos = await listCases(client, { ...options, limit: LOTE, offset: lote * LOTE });
    for (const c of casos) porId.set(c.id, c);
    if (casos.length < LOTE) break;
  }
  return [...porId.values()];
}
